/**
 * ShipProjectile.ts
 * Pooled forward-firing projectile for the Courier Ship (World 2 space shmup).
 *
 * Differs from `Projectile` (Nova's platformer Star Shards):
 *   - Always travels right (ship faces right; the camera scrolls right).
 *   - No platform collision (space has no platforms).
 *   - Despawn rules are camera-relative: dies when its right edge is more
 *     than `SHIP_PROJECTILE_OFFSCREEN_MARGIN` past the right edge of the
 *     visible window, or after `SHIP_PROJECTILE_TTL_S` seconds.
 *
 * Pool contract: implements `reset()` for `ObjectPool` recycling. Always
 * acquire from the pool and call `spawn()` rather than `new`-ing directly
 * (the constructor exists only so `shipCombat.ts` can pre-warm the pool at
 * module load — see `shipProjectilePool.prewarm(...)`).
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module ShipProjectile
 */

import { BaseEntity } from './BaseEntity';
import type { LevelRuntime } from '../engine/gameState';
import { spaceState } from '../engine/spaceState';
import { VIEWPORT_WIDTH } from '../../constants/gameConstants';
import {
  SHIP_PROJECTILE_WIDTH,
  SHIP_PROJECTILE_HEIGHT,
  SHIP_PROJECTILE_SPEED,
  SHIP_PROJECTILE_TTL_S,
} from '../../constants/spaceConstants';

/**
 * Off-screen margin (px) added to the right edge of the visible window
 * before a projectile is considered despawned. Gives a small grace zone so
 * a projectile that strikes an enemy on the very edge still registers the hit.
 */
export const SHIP_PROJECTILE_OFFSCREEN_MARGIN = 32;

/**
 * ShipProjectile — pooled projectile entity for the ship's main weapon.
 *
 * Multiple instances exist at once (up to `SHIP_MAX_PROJECTILES`), all managed
 * by the `shipProjectilePool` in `shipCombat.ts`.
 *
 * Lifecycle:
 *   1. Acquired from pool via `pool.acquire()`.
 *   2. Initialised with `spawn(x, y)`.
 *   3. Updated each tick via `update(dt, level)` (level is unused today).
 *   4. When `dead === true`, returned to pool via `pool.release(p)`.
 */
export class ShipProjectile extends BaseEntity {
  /** Monotonic ID counter for stable React keys across pool recycling. */
  private static nextId = 1;

  /**
   * Unique spawn identifier — re-issued each time the projectile is recycled.
   * Used by the renderer for stable list keys (mirrors `Projectile.spawnId`).
   */
  public spawnId = 0;

  /** True while this projectile is actively travelling. */
  public active = false;

  /** Seconds since spawn — used for TTL despawn. */
  private lifeTime = 0;

  constructor() {
    super(0, 0, SHIP_PROJECTILE_WIDTH, SHIP_PROJECTILE_HEIGHT);
    this.dead = true; // starts dead until spawned
  }

  /**
   * Initialise the projectile at a world position. Always travels right.
   *
   * @param x - Spawn X in world space (logical px).
   * @param y - Spawn Y in world space (logical px).
   */
  public spawn(x: number, y: number): void {
    this.spawnId = ShipProjectile.nextId++;
    this.x = x;
    this.y = y;
    this.vx = SHIP_PROJECTILE_SPEED;
    this.vy = 0;
    this.dead = false;
    this.active = true;
    this.lifeTime = 0;
  }

  /** Reset state for pool recycling. Called by `ObjectPool.release()`. */
  public reset(): void {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.dead = true;
    this.active = false;
    this.lifeTime = 0;
  }

  /**
   * Advance the projectile by one fixed timestep.
   * Despawns on TTL expiry or after exiting the visible window.
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Unused today; kept for `BaseEntity.update` contract.
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    if (!this.active) return;

    this.lifeTime += dt;
    if (this.lifeTime >= SHIP_PROJECTILE_TTL_S) {
      this.dead = true;
      this.active = false;
      return;
    }

    this.x += this.vx * dt;
    // vy is 0 today, but integrate so future homing variants don't need a fork.
    this.y += this.vy * dt;

    // DECISION: Camera-relative despawn. The platformer projectile uses
    // level-bounds; in space there are no level bounds, only a sliding window.
    // Compare the projectile's RIGHT edge (x + width) to the cull line so wider
    // sprites do not vanish a few pixels early — mirrors `Projectile.update()`,
    // which also uses `x + width` for its bounds check.
    const rightCullX = spaceState.cameraX + VIEWPORT_WIDTH + SHIP_PROJECTILE_OFFSCREEN_MARGIN;
    if (this.x + this.width > rightCullX) {
      this.dead = true;
      this.active = false;
    }
  }
}
